/**
 * Whether a deviation result shows its own m³ (spec goal 3, §4.3).
 *
 * A paired run's volumes come from cut & fill, so its deviation panel hides
 * them and says "Volumes: see Cut & fill" — but only while that cut & fill
 * layer actually exists AND has usable totals. If the cut & fill half failed,
 * or produced no finite totals, the deviation's own volumes are the only ones
 * there are, and they stay.
 *
 * "Usable" is the server's rule for the combined PDF, kept identical so the
 * panel and the document never disagree: `surfaceDifferenceSectionFromStats`
 * (processReport/surfaceDifferenceSection.ts, called from
 * `comparisonReport.ts`) drops a cut & fill section unless cut, fill, net and
 * area are all finite numbers, and the deviation section then keeps its m³.
 *
 * "Is there a cut & fill partner" is `pairing.ts`'s question; this only
 * names what the answer means for the deviation card.
 */
import { comparisonPartnerId } from './pairing';

type Layer = Parameters<typeof comparisonPartnerId>[1][number];

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** The cut & fill layer's totals are complete enough to stand in for the deviation m³. */
export function hasUsableCutFillTotals(layer: Pick<Layer, 'metadata'>): boolean {
  const meta = (layer.metadata ?? {}) as { surfaceDifference?: { stats?: Record<string, unknown> } };
  const stats = meta.surfaceDifference?.stats;
  if (!stats || typeof stats !== 'object') return false;
  return isNum(stats.cutVolumeM3) && isNum(stats.fillVolumeM3) && isNum(stats.netVolumeM3) && isNum(stats.areaM2);
}

export function showDeviationVolumes(layer: Layer, layers: readonly Layer[]): boolean {
  const partnerId = comparisonPartnerId(layer, layers);
  if (partnerId === undefined) return true;
  const partner = layers.find((l) => l.id === partnerId);
  return !partner || !hasUsableCutFillTotals(partner);
}
